<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'BalanceSnapshotRun_Title' | translateWF: 'Balance Snapshot Runs' }}</h1>
        <div class="page__title_buttons">
          <p-button icon="pi pi-refresh" variant="outlined" [label]="'Common_Refresh' | translateWF: 'Refresh'" (click)="loadData()" />
        </div>
      </div>
    </div>

    <div class="grid-12">
      <div class="span-12-12-3 col">
        <p-panel [header]="site?.name ?? ''">
          <div class="col-xs">
            <p-card>
              <div class="form-box">
                <label>{{ 'BalanceSnapshotRun_LatestMonth' | translateWF: 'Latest computed month' }}</label>
                @if (latest(); as l) {
                  <span class="text-2xl font-semibold">{{ formatMonth(l.year, l.month) }}</span>
                } @else {
                  <span>{{ 'BalanceSnapshotRun_NoSnapshots' | translateWF: 'No snapshots yet' }}</span>
                }
              </div>
            </p-card>
            @if (latest(); as l) {
              <p-card>
                <div class="form-box">
                  <label>{{ 'BalanceSnapshotRun_ComputedAt' | translateWF: 'Computed' }}</label>
                  <span>{{ l.computedAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                </div>
              </p-card>
              @if (isAdmin) {
                <p-card>
                  <div class="form-box">
                    <label>{{ 'BalanceSnapshotRun_ComputedBy' | translateWF: 'Computed by' }}</label>
                    <span>{{ l.computedBy || '-' }}</span>
                  </div>
                </p-card>
              }
            }
          </div>
        </p-panel>

        @if (isAdmin) {
          <p-panel [header]="'BalanceSnapshotRun_Compute' | translateWF: 'Compute month'">
            <p-card>
              <form class="col" [formGroup]="form" (ngSubmit)="computeMonth()">
                <div class="form-box">
                  <label for="compute-month">{{ fieldLabels.month | translateWF: 'Month' }} *</label>
                  <p-datepicker
                    inputId="compute-month"
                    formControlName="month"
                    view="month"
                    dateFormat="yy-mm"
                    [maxDate]="maxMonth"
                    [readonlyInput]="true"
                    [fluid]="true"
                    styleClass="w-full"
                    class="w-full"
                    [placeholder]="fieldLabels.month | translateWF: 'Month'"
                    [ngClass]="{ 'ng-invalid ng-dirty': form.controls.month.invalid && form.controls.month.touched }"
                  />
                </div>
                <p-button
                  type="submit"
                  icon="pi pi-calculator"
                  [fluid]="true"
                  [label]="'BalanceSnapshotRun_Compute' | translateWF: 'Compute month'"
                  [disabled]="hasRunInProgress()"
                />
              </form>
            </p-card>
          </p-panel>
        }
      </div>

      <div class="span-12-12-9">
        <p-panel [header]="'BalanceSnapshotRun_Runs' | translateWF: 'Runs'">
          <p-card data-variant="compact">
            <p-table [value]="runs()" dataKey="id" [lazy]="true" (onSort)="onSort($event)" [rowHover]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 3rem"></th>
                  <th pSortableColumn="StartedAt">
                    {{ 'BalanceSnapshotRun_StartedAt' | translateWF: 'Started' }}
                    <p-sortIcon field="StartedAt" />
                  </th>
                  <th>{{ 'BalanceSnapshotRun_Type' | translateWF: 'Type' }}</th>
                  <th>{{ 'BalanceSnapshotRun_Months' | translateWF: 'Months' }}</th>
                  <th pSortableColumn="Status">
                    {{ 'BalanceSnapshotRun_Status' | translateWF: 'Status' }}
                    <p-sortIcon field="Status" />
                  </th>
                  <th>{{ 'BalanceSnapshotRun_Progress' | translateWF: 'Progress' }}</th>
                  <th>{{ 'BalanceSnapshotRun_Duration' | translateWF: 'Duration' }}</th>
                  @if (isAdmin) {
                    <th>{{ 'BalanceSnapshotRun_RequestedBy' | translateWF: 'Requested by' }}</th>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-row let-expanded="expanded">
                @let run = runStrict(row);
                <tr>
                  <td>
                    <p-button type="button" [pRowToggler]="run" [text]="true" [rounded]="true" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
                  </td>
                  <td>{{ run.startedAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                  <td>{{ typeLabel(run.type) }}</td>
                  <td>{{ monthRange(run) }}</td>
                  <td>
                    <p-tag [value]="statusLabel(run.status)" [severity]="statusSeverity(run.status)" [pTooltip]="run.errorMessage ?? ''" [tooltipDisabled]="!run.errorMessage" />
                  </td>
                  <td>{{ run.months.length }} / {{ monthCount(run) }}</td>
                  <td>{{ durationLabel(run) }}</td>
                  @if (isAdmin) {
                    <td>{{ run.createdBy || '-' }}</td>
                  }
                </tr>
              </ng-template>

              <ng-template pTemplate="rowexpansion" let-row>
                @let run = runStrict(row);
                <tr>
                  <td [attr.colspan]="columnCount">
                    @if (run.errorMessage) {
                      <p-message severity="error" [text]="run.errorMessage" />
                    }
                    <p-table [value]="run.months" [rowHover]="true">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ 'BalanceSnapshotRun_Month' | translateWF: 'Month' }}</th>
                          <th class="text-right">{{ 'BalanceSnapshotRun_Locations' | translateWF: 'Locations' }}</th>
                          <th class="text-right">{{ 'BalanceSnapshotRun_Pages' | translateWF: 'Pages' }}</th>
                          <th class="text-right">{{ 'BalanceSnapshotRun_LotItems' | translateWF: 'Lot items' }}</th>
                          <th class="text-right">{{ 'BalanceSnapshotRun_Duration' | translateWF: 'Duration' }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-month>
                        <tr>
                          <td>{{ formatMonth(month.year, month.month) }}</td>
                          <td class="text-right tabular-nums">{{ month.locationsProcessed | number }}</td>
                          <td class="text-right tabular-nums">{{ month.snapshotDocumentsCreated | number }}</td>
                          <td class="text-right tabular-nums">{{ month.totalLotItemsCaptured | number }}</td>
                          <td class="text-right tabular-nums">{{ month.durationMs / 1000 | number: '1.0-1' }} s</td>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="5" class="text-center">
                            {{ 'BalanceSnapshotRun_NoMonthsYet' | translateWF: 'No month computed yet' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="columnCount" class="text-center">
                    {{ 'General_NoRecords' | translateWF: 'No records found' }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
            <p-paginator
              [rows]="pageSize()"
              [totalRecords]="totalRecords()"
              [first]="(currentPage() - 1) * pageSize()"
              (onPageChange)="onPageChange($event)"
              [rowsPerPageOptions]="[25, 50, 100]"
            />
          </p-card>
        </p-panel>
      </div>
    </div>

    <p-confirmDialog />
  </div>
</main>
